import httpClient from 'api/https-client';
import { MouseEvent, useCallback } from 'react';
import { MESO_MVP_SHORTCODE, urlHasMesoShortcode } from 'utils/handoffShortcodes';
import { trackAmplitudeEvent } from 'utils/trackAmplitudeEvent';

// Re-exported for existing imports; the matching lives in utils/handoffShortcodes.
export { MESO_MVP_SHORTCODE, urlHasMesoShortcode };

const hrefHasMesoShortcode = (anchor: HTMLAnchorElement): boolean =>
  urlHasMesoShortcode(anchor.getAttribute('href') || '') || urlHasMesoShortcode(anchor.href || '');

export const useMesoHandoff = () => {
  const openHandoff = useCallback(async () => {
    try {
      // Mint a FRESH, short-lived (60 s, single-use) handoff URL on every click — never cache it.
      // The endpoint is gold/MVP-gated server-side (403 for everyone else → this no-ops).
      const { status, data } = await httpClient.get<{ url: string }>('/meso/handoff-url');

      if (status >= 200 && status < 300 && data?.url) {
        window.open(data.url, '_blank', 'noopener,noreferrer');
      }
    } catch (error) {
      // Graceful degrade — surface nothing scary to the member; logged for debugging.
      console.error('Failed to build Meso handoff URL', error);
    }
  }, []);

  /**
   * Delegated click handler — attach to the element that renders manual content.
   * Intercepts clicks on any link whose URL contains the Meso shortcode and
   * swaps the dead shortcode link for a freshly minted, authenticated handoff.
   */
  const handleManualClick = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      const anchor = (event.target as HTMLElement | null)?.closest?.('a') as HTMLAnchorElement | null;

      if (!anchor || !hrefHasMesoShortcode(anchor)) {
        return;
      }

      event.preventDefault();
      trackAmplitudeEvent('Meso Claim Pressed');
      void openHandoff();
    },
    [openHandoff],
  );

  return { handleManualClick, openHandoff };
};
